import React, { useEffect } from 'react';
import { AppRouter } from './routes';
import { ConfirmModal, SlotSelectionModal } from './components';
import { Toaster } from '@/components/ui/toaster';
import { useFetchUser } from './hooksApi';
import { useUserStore } from './stores';
import { useAuthStore } from './stores/authStore';
import * as Sentry from '@sentry/react';

const App: React.FC = () => {
  const { token } = useAuthStore();
  const { data } = useFetchUser(!!token); // Only fetch if token exists
  const { setUser } = useUserStore();

  // TODO: Zendesk temporarily disabled due to migration.
  // Defensive cleanup to remove any lingering Zendesk widget elements.
  useEffect(() => {
    const cleanupZendesk = () => {
      const zendeskFrame = document.querySelector('iframe[id^="webWidget"]');
      if (zendeskFrame) {
        zendeskFrame.remove();
      }

      const launcher = document.querySelector('[class*="zEWidget-launcher"]');
      if (launcher) {
        launcher.remove();
      }

      // Also remove the launcher frame
      const launcherFrame = document.querySelector('iframe[id^="launcher"]');
      if (launcherFrame) {
        launcherFrame.remove();
      }
    };

    // Run cleanup immediately and after a short delay (in case widget loads async)
    cleanupZendesk();
    const timeoutId = setTimeout(cleanupZendesk, 1000);

    return () => clearTimeout(timeoutId);
  }, []);

  // Set user data when fetched
  useEffect(() => {
    if (data?.object) {
      setUser(data.object);

      // Set Sentry user context for error tracking
      Sentry.setUser({
        id: data.object.uid,
        email: data.object.email || undefined,
        username: `${data.object.first_name} ${data.object.last_name}`,
      });

      // Add merchant context as tags
      Sentry.setTag('user_type', data.object.type);
      if (data.object.roles && data.object.roles.length > 0) {
        Sentry.setTag('user_role', data.object.roles[0]);
      }
    } else {
      // Clear user context on logout
      Sentry.setUser(null);
    }
  }, [data, setUser]);

  return (
    <>
      <AppRouter />
      <ConfirmModal />
      <SlotSelectionModal />
      <Toaster />
    </>
  );
};

export default App;
